import { useEffect, useState } from "react"

import {
  Attendee,
  AudioVideoFacade,
  Transcript,
  TranscriptAlternative,
  TranscriptEvent,
  TranscriptResult,
} from "amazon-chime-sdk-js"

type CaptionChunk = {
  resultId: number | string
  text: string
  spokenAt: string
  speakerLabel: string
  attendeeId?: string
  vocabularyFilterMatches: string[]
}

type UseChimeCaptionsProps = {
  audioVideo?: AudioVideoFacade
  buildSpeakerLabel: (attendee?: Attendee) => string
}

const useChimeCaptions = ({
  audioVideo,
  buildSpeakerLabel,
}: UseChimeCaptionsProps) => {
  const [captionChunks, setCaptionChunks] = useState<CaptionChunk[]>([])

  useEffect(() => {
    if (!audioVideo) return
    if (!audioVideo.transcriptionController) {
      throw new Error("Missing Transcription controller")
    }

    const handleTranscriptEvent = (transcriptEvent: TranscriptEvent) => {
      if (!(transcriptEvent instanceof Transcript)) return

      const results = transcriptEvent.results || []
      results.forEach((result: TranscriptResult) => {
        const alternatives = result.alternatives || []
        alternatives.forEach((alt: TranscriptAlternative) => {
          const text = alt.transcript?.trim()
          if (!text) return

          const attendee = alt.items?.[0]?.attendee
          const speakerLabel = buildSpeakerLabel(attendee)
          const vocabularyFilterMatches = (alt.items || [])
            .filter(item => item.vocabularyFilterMatch)
            .map(item => item.content)

          const spokenAt = new Date(result.startTimeMs).toISOString()

          setCaptionChunks(prev => {
            const existingIdx = prev.findIndex(
              chunk => chunk.resultId === result.resultId
            )
            // if this chunk is a continuation of an existing chunk, update instead of creating a new line
            if (existingIdx !== -1) {
              const updated = [...prev]
              updated[existingIdx] = {
                ...updated[existingIdx],
                text,
                spokenAt,
                vocabularyFilterMatches,
              }
              return updated
            }

            const newChunk = {
              resultId: result.resultId,
              text,
              spokenAt,
              speakerLabel,
              attendeeId: attendee?.attendeeId,
              vocabularyFilterMatches,
            }
            const next: CaptionChunk[] = [...prev, newChunk]
            next.sort(
              (a, b) =>
                new Date(a.spokenAt).getTime() - new Date(b.spokenAt).getTime()
            )
            return next.slice(-100)
          })
        })
      })
    }

    audioVideo.transcriptionController.subscribeToTranscriptEvent(
      handleTranscriptEvent
    )

    return () => {
      audioVideo.transcriptionController?.unsubscribeFromTranscriptEvent(
        handleTranscriptEvent
      )
    }
  }, [audioVideo, buildSpeakerLabel])

  return { captionChunks }
}

export default useChimeCaptions
